class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification') || this.querySelector('#cart-notification');
    this.header = document.querySelector('sticky-header') || null;
    this.loadingOverlay = (this.notification && this.notification.querySelector('.cart-notification__loading-overlay')) || null;

    this.boundKeyup = (evt) => evt.code === 'Escape' && this.close();
    this.boundBodyClick = this.handleBodyClick.bind(this);
    this.boundClose = this.close.bind(this);
    this.debouncedOnChange = (typeof debounce === 'function') ? debounce((event) => this.onChange(event), 300) : ((event) => this.onChange(event));

    this.rebindUI();
    this.initializeAgeCheck();
    this.initializeRecommendationsAddToCart();

    window.cartNotification = this;
  }

  initializeRecommendationsAddToCart() {
    document.body.addEventListener('submit', (evt) => {
      const form = evt.target;
      if (form.classList.contains('add-to-cart-form') && 
          (form.closest('.cart-recommendations') || form.closest('.cart__footer-gifting'))) {
        evt.preventDefault();
        
        if (this.loadingOverlay) this.loadingOverlay.classList.remove('hidden');
        
        const formData = new FormData(form);
        
        fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        })
        .then(response => {
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          return response.json();
        })
        .then(() => {
          return fetch(`${window.location.pathname}?sections=${this.getSectionsToRender().map(s => s.section).join(',')}`);
        })
        .then(response => response.json())
        .then(sections => {
          return fetch('/cart.js', { 
            headers: { 'X-Requested-With': 'XMLHttpRequest' }
          })
          .then(res => res.json())
          .then(cart => {
            const parsedState = {
              item_count: cart.item_count,
              total_price: cart.total_price,
              sections: sections
            };
            
            this.renderContents(parsedState);
            this.open();
            
            return parsedState;
          });
        })
        .catch(error => {
          const errorContainer = document.getElementById('cart-errors');
          if (errorContainer) {
            errorContainer.textContent = 'There was an error adding this item to your cart. Please try again.';
            errorContainer.style.display = 'block';
            
            setTimeout(() => {
              errorContainer.style.display = 'none';
            }, 5000);
          }
        })
        .finally(() => {
          if (this.loadingOverlay) this.loadingOverlay.classList.add('hidden');
        });
      }
    });
  }

  getSectionsToRender() {
    return [
      { id: 'cart-notification-button', section: 'cart-notification-button', selector: '.shopify-section' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      { id: 'cart-notification-product', section: 'cart-notification-product', selector: '.js-contents' },
      { id: 'cart-notification-links', section: 'cart-notification-links', selector: '.js-contents' },
      { id: 'cart-free-shipping', section: 'cart-free-shipping', selector: '.shopify-section' }
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector(selector);
    return el ? el.innerHTML : '';
  }

  rebindUI() {
    this.notification = document.getElementById('cart-notification') || this.querySelector('#cart-notification') || this.notification;
    this.loadingOverlay = (this.notification && this.notification.querySelector('.cart-notification__loading-overlay')) || this.loadingOverlay;

    try { if (this.notification) this.notification.removeEventListener('keyup', this.boundKeyup); } catch (e) {}
    if (this.notification) this.notification.addEventListener('keyup', this.boundKeyup);

    this.bindCloseButtons();
    this.bindEmptyStateClose();
    this.bindRemoveButtonsAndInputs();
    this.initializeAgeCheck();
  }

  bindCloseButtons() {
    if (!this.notification) return;
    
    const closeButtons = this.notification.querySelectorAll('.cart-notification__close, .modal__close-button, [data-action="close"]');
    closeButtons.forEach(btn => {
      try { btn.removeEventListener('click', this.boundClose); } catch (e) {}
      btn.addEventListener('click', (evt) => {
        if (typeof cartEnableScroll === 'function') cartEnableScroll();
        this.close();
      });
    });
  }

  bindEmptyStateClose() {
    if (!this.notification) return;
    
    const emptyBtns = this.notification.querySelectorAll(
      '.cart-notification__empty button, ' + 
      '.cart-notification__continue-shopping, ' + 
      'button[aria-label="Close"], ' + 
      '#cart-notification-empty button, ' +
      '.cart-empty-button, ' +
      '.cart-notification__empty-message + button'
    );
    
    if (emptyBtns.length === 0) return;

    if (!this.boundCloseAndEnableScroll) {
      this.boundCloseAndEnableScroll = () => {
        if (typeof cartEnableScroll === 'function') cartEnableScroll();
        this.close();
      };
    }

    emptyBtns.forEach(btn => {
      try { btn.removeEventListener('click', this.boundCloseAndEnableScroll); } catch (e) {}
      btn.addEventListener('click', this.boundCloseAndEnableScroll);
      btn.setAttribute('data-bound-empty-close', 'true');
    });
  }

  bindRemoveButtonsAndInputs() {
    if (!this.notification) return;

    this.notification.querySelectorAll('.quantity__input').forEach(input => {
      try { input.removeEventListener('change', this.debouncedOnChange); } catch (e) {}
      input.addEventListener('change', this.debouncedOnChange);
    });

    const removeSelectors = 'cart-remove-button, [data-cart-remove], .cart-item__remove, .cart-notification__remove, .cart__remove';
    this.notification.querySelectorAll(removeSelectors).forEach(removeEl => {
      const idx = (removeEl.dataset && (removeEl.dataset.index || removeEl.dataset.line)) || removeEl.getAttribute('data-index') || removeEl.getAttribute('data-line');
      const clickable = (removeEl.tagName && removeEl.tagName.toLowerCase() === 'cart-remove-button') ? (removeEl.querySelector('a') || removeEl) : removeEl;
      if (!clickable) return;
      const handler = (event) => {
        event.preventDefault();
        if (idx !== undefined && idx !== null) this.updateQuantity(idx, 0);
      };
      try { clickable.removeEventListener('click', handler); } catch (e) {}
      clickable.addEventListener('click', handler);
    });
  }

  open() {
    if (!this.notification) return;
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener('transitionend', () => {
      try { this.notification.focus(); } catch (e) {}
      if (typeof trapFocus === 'function') trapFocus(this.notification);
    }, { once: true });

    try { document.body.removeEventListener('click', this.boundBodyClick); } catch (e) {}
    
    setTimeout(() => {
      document.body.addEventListener('click', this.boundBodyClick);
    }, 0);
    
    const productRecommendationsSectionCart = document.querySelector('.product-recommendations-cart');
    
    if (productRecommendationsSectionCart) {
      const url = productRecommendationsSectionCart.dataset.url;
      if (url) {    
        fetch(url)
          .then(response => response.text())
          .then(text => {
            const html = document.createElement('div');
            html.innerHTML = text;
            const recommendations = html.querySelector('.product-recommendations-cart');
            
            if (recommendations && recommendations.innerHTML.trim().length) {
              productRecommendationsSectionCart.innerHTML = recommendations.innerHTML;
            }
          })
          .catch(e => {});
      }
    }
  }

  close() {
    if (!this.notification) return;
    
    this.notification.classList.remove('animate', 'active');
    
    try { document.body.removeEventListener('click', this.boundBodyClick); } catch (e) {}
    
    if (typeof removeTrapFocus === 'function') {
      try {
        removeTrapFocus(this.activeElement || this.notification);
      } catch (e) {}
    }
    
    if (typeof cartEnableScroll === 'function') {
      try {
        cartEnableScroll();
      } catch (e) {}
    }
    
    setTimeout(() => {
      if (this.notification) {
        this.notification.classList.remove('animate', 'active');
      }
      
      try { document.body.classList.remove('prevent-scroll--cart'); } catch (e) {}
      try { document.documentElement.classList.remove('prevent-scroll--cart'); } catch (e) {}
    }, 300);
  }

  handleBodyClick(evt) {
    const isInsideCart = evt.target.closest('#cart-notification');
    
    if (!isInsideCart) {
      const disclosure = evt.target.closest('details-disclosure');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
      
      try { document.body.classList.remove('prevent-scroll--cart'); } catch (e) {}
      try { document.documentElement.classList.remove('prevent-scroll--cart'); } catch (e) {}
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  updateSubtotal(parsedState) {
    const subtotalElement = document.querySelector('.totals__subtotal-value');
    if (subtotalElement && parsedState.total_price !== undefined) {
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        subtotalElement.innerHTML = Shopify.formatMoney(parsedState.total_price, Shopify.money_with_currency_format || Shopify.money_format);
      } else {
        subtotalElement.innerHTML = new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: (window.Shopify && Shopify.currency && Shopify.currency.active) ? Shopify.currency.active : 'USD'
        }).format(parsedState.total_price / 100);
      }
    }
  }

  onChange(event) {
    const input = event.target;
    const idx = input && (input.dataset.index || input.getAttribute('data-index'));
    const value = input && input.value;
    if (idx == null) return;
    this.updateQuantity(idx, value);
  }

  updateQuantity(line, quantity) {
    if (this.loadingOverlay) this.loadingOverlay.classList.remove('hidden');

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map(s => s.section),
      sections_url: window.location.pathname
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then(response => response.text())
      .then(state => {
        const parsedState = JSON.parse(state);
        this.renderContents(parsedState);
      })
      .catch(err => {})
      .finally(() => {
        if (this.loadingOverlay) this.loadingOverlay.classList.add('hidden');
      });
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    const isCartEmpty = parsedState.item_count === 0;
    
    this.updateEmptyState(isCartEmpty);

    if (!parsedState.sections || Object.keys(parsedState.sections).length === 0) {
      fetch(window.location.pathname + '?sections=' + this.getSectionsToRender().map(s => s.section).join(','))
        .then(response => response.json())
        .then(sections => {
          const fallbackState = {
            ...parsedState,
            sections: sections
          };
          
          this.renderSectionsFromHTML(fallbackState, isCartEmpty);
        })
        .catch(err => {
          window.location.reload();
        });
        
      return;
    }

    this.renderSectionsFromHTML(parsedState, isCartEmpty);
  }

  updateEmptyState(isCartEmpty) {
    const productSection = document.getElementById('cart-notification-product');
    const emptySection = document.getElementById('cart-notification-empty');
    const linksSection = document.getElementById('cart-notification-links');
    const buttonSection = document.getElementById('cart-notification-button');
    const shippingSection = document.getElementById('cart-free-shipping');
    
    if (isCartEmpty) {
      if (productSection) productSection.style.display = 'none';
      if (emptySection) emptySection.style.display = 'block';
      if (buttonSection) buttonSection.style.display = 'none';
      if (shippingSection) shippingSection.style.display = 'none';
      
      if (linksSection) {
        const emptyMsg = linksSection.querySelector('.cart-notification__empty');
        if (emptyMsg) {
          emptyMsg.style.display = 'block';
          
          const emptyButtons = emptyMsg.querySelectorAll('button');
          if (emptyButtons.length > 0) {
            emptyButtons.forEach(btn => {
              if (!btn.hasAttribute('data-bound-empty-close')) {
                try { btn.removeEventListener('click', this.boundCloseAndEnableScroll); } catch (e) {}
                
                if (!this.boundCloseAndEnableScroll) {
                  this.boundCloseAndEnableScroll = () => {
                    if (typeof cartEnableScroll === 'function') cartEnableScroll();
                    this.close();
                  };
                }
                
                btn.addEventListener('click', this.boundCloseAndEnableScroll);
                btn.setAttribute('data-bound-empty-close', 'true');
              }
            });
          }
        }
      }
      
      setTimeout(() => this.bindEmptyStateClose(), 100);
      
    } else {
      if (productSection) productSection.style.display = 'block';
      if (emptySection) emptySection.style.display = 'none';
      if (buttonSection) buttonSection.style.display = 'block';
      if (shippingSection) shippingSection.style.display = 'block';
      
      if (linksSection) {
        const emptyMsg = linksSection.querySelector('.cart-notification__empty');
        if (emptyMsg) emptyMsg.style.display = 'none';
      }
    }
  }

  renderSectionsFromHTML(parsedState, isCartEmpty) {
    this.getSectionsToRender().forEach(section => {
      const serverHtml = parsedState.sections && parsedState.sections[section.section];
      const targetEl = document.getElementById(section.id);
      
      if (!isCartEmpty && section.id === 'cart-notification-empty') {
        return;
      }
      
      if (isCartEmpty && section.id === 'cart-notification-product') {
        return;
      }
      
      if (serverHtml && targetEl) {
        const html = this.getSectionInnerHTML(serverHtml, section.selector);
        
        if (html && html.trim() !== '') {
          targetEl.innerHTML = html;
        }
      }
    });

    this.updateSubtotal(parsedState);
    this.rebindUI();

    if (this.header && typeof this.header.reveal === 'function') this.header.reveal();
    this.open();
  }

  initializeAgeCheck() {
    const ageCheckbox = document.getElementById('age-check');
    const checkoutButton = document.querySelector('#cart-notification-form button[name="checkout"]');

    try { if (this.boundAgeCheckChange && ageCheckbox) ageCheckbox.removeEventListener('change', this.boundAgeCheckChange); } catch (e) {}

    if (!checkoutButton) return;

    this.boundAgeCheckChange = () => this.updateCheckoutButtonState();

    if (ageCheckbox) ageCheckbox.addEventListener('change', this.boundAgeCheckChange);

    this.updateCheckoutButtonState();
  }

  updateCheckoutButtonState() {
    const ageCheckbox = document.getElementById('age-check');
    const checkoutButton = document.querySelector('#cart-notification-form button[name="checkout"]');

    if (ageCheckbox && checkoutButton) {
      if (ageCheckbox.checked) {
        checkoutButton.disabled = false;
        checkoutButton.style.opacity = '1';
        checkoutButton.style.pointerEvents = 'auto';
      } else {
        checkoutButton.disabled = true;
        checkoutButton.style.opacity = '0.5';
        checkoutButton.style.pointerEvents = 'none';
      }
    } else if (checkoutButton && !ageCheckbox) {
      checkoutButton.disabled = false;
      checkoutButton.style.opacity = '1';
      checkoutButton.style.pointerEvents = 'auto';
    }
  }
}

if (!customElements.get('cart-notification')) {
  customElements.define('cart-notification', CartNotification);
}